import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Lock, User } from "lucide-react";
import { LOGO_URL, BRAND } from "@/lib/brand";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(identifier, password);
      toast.success(`Welcome back, ${u.name}`);
      nav(u.role === "employee" ? "/employee" : "/admin", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  const quickLogin = (id, pw) => { setIdentifier(id); setPassword(pw); };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr]">
      {/* Left panel */}
      <div className="relative hidden lg:flex flex-col justify-between p-10 border-r border-border overflow-hidden">
        <div className="absolute inset-0 grid-lines opacity-40" />
        <div
          className="absolute inset-0 bg-cover bg-center opacity-25"
          style={{ backgroundImage: "url(https://images.unsplash.com/photo-1768280511074-3b3effe7a139?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMHRvcG9ncmFwaGljJTIwbWFwJTIwZGFya3xlbnwwfHx8fDE3ODQ4NDU3NDd8MA&ixlib=rb-4.1.0&q=85)" }}
        />
        <div className="absolute inset-0 noise" />

        {/* Removed left brand: logo now in top-right of form panel */}
        <div className="relative">
          <div className="label-mono">Enterprise attendance · GPS-verified</div>
        </div>

        <div className="relative space-y-6 max-w-md">
          <div>
            <div className="label-mono mb-2">GPS-verified · Multi-country · Enterprise</div>
            <h2 className="font-display text-4xl leading-[1.05] tracking-tight">
              Attendance that only counts when you're actually there.
            </h2>
          </div>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Geofenced punch-in / out across every office. Signed, timestamped, tamper-evident attendance for your entire workforce.
          </p>
        </div>
        <div className="relative label-mono">
          GPS-verified attendance across every office · timezone-aware
        </div>
      </div>

      {/* Right panel — form */}
      <div className="relative flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-sm space-y-8">
          <div>
            <div className="label-mono mb-2">Sign in</div>
            <h1 className="font-display text-3xl tracking-tight">Authenticate</h1>
            <p className="text-sm text-muted-foreground mt-1">Use your Employee ID or email address.</p>
          </div>
          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="identifier" className="label-mono">Employee ID / Email</Label>
              <div className="relative mt-2">
                <User className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" strokeWidth={1.5} />
                <Input
                  id="identifier"
                  data-testid="login-identifier-input"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  className="pl-9 rounded-none bg-secondary/50 border-border h-11 font-mono-brand"
                  placeholder="SA001"
                  required
                />
              </div>
            </div>
            <div>
              <Label htmlFor="password" className="label-mono">Password</Label>
              <div className="relative mt-2">
                <Lock className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" strokeWidth={1.5} />
                <Input
                  id="password"
                  data-testid="login-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-9 rounded-none bg-secondary/50 border-border h-11 font-mono-brand"
                  placeholder="••••••••"
                  required
                />
              </div>
            </div>
            <Button
              data-testid="login-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full h-11 rounded-none btn-primary-brand"
            >
              {loading ? "Signing in…" : "Sign in →"}
            </Button>
          </form>

          <div className="border-t border-border pt-6">
            <div className="label-mono mb-3">Need help signing in?</div>
            <div className="text-xs text-muted-foreground">
              Use the credentials shared by your organization admin. Forgot them? Reach out to your admin to reset.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
